import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../utils/ab-comparison-slider.css';
import { glyphPaths } from '../icons/icon-paths';
import { gsap } from 'gsap';
import GlyphSvg from './Glyphs';

const ABComparisonSlider = ({ 
  beforeImage, 
  afterImage, 
  beforeLabel = "Before", 
  afterLabel = "After",
  aspectRatio = "3:4",
  borderRadius = "0",
  id = "default", // Add unique ID prop
  caption = null, // Add caption prop
  customHeight = null,
  minHeight = null,
  customMaxHeight = null
}) => {
  const [sliderPosition, setSliderPosition] = useState(0); // Start at 0% (far left)
  const [isDragging, setIsDragging] = useState(false);
  const [currentGlyph, setCurrentGlyph] = useState('g08');
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef(null);
  const beforeImageRef = useRef(null);
  const afterImageRef = useRef(null);
  const isDraggingRef = useRef(false);
  const touchStartRef = useRef(null);
  const gestureDirectionRef = useRef(null); // 'horizontal', 'vertical', or null
  const [isFullscreen, setIsFullscreen] = useState(false);
  const animationRef = useRef({ value: 0 });

  // Create a unique key for this slider instance
  const sliderKey = `slider-${id}`;
  
  // Debug log to see the ID and minHeight
  console.log(`ABComparisonSlider created with ID: ${id}, sliderKey: ${sliderKey}, minHeight: ${minHeight}`);

  const baseCycle = ['g01', 'g02', 'g03', 'g04', 'g05', 'g06', 'g07', 'g08', 'g09', 'g10', 'g11'];
  const cycleRate = 2;
  const glyphCycle = Array(cycleRate).fill(baseCycle).flat();

  const setGlyphFromPercent = useCallback((percent) => {
    const index = Math.floor((percent / 100) * glyphCycle.length * cycleRate) % glyphCycle.length;
    setCurrentGlyph(glyphCycle[index]);
  }, [glyphCycle, cycleRate]);

  const getPreviousGlyph = (current) => {
    const currentIndex = baseCycle.indexOf(current);
    const previousIndex = currentIndex === 0 ? baseCycle.length - 1 : currentIndex - 1;
    return baseCycle[previousIndex];
  };

  const getNextGlyph = (current) => {
    const currentIndex = baseCycle.indexOf(current);
    const nextIndex = currentIndex === baseCycle.length - 1 ? 0 : currentIndex + 1;
    return baseCycle[nextIndex];
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    
    setSliderPosition(clampedPercentage);
    setGlyphFromPercent(clampedPercentage);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    
    // Only allow dragging after the initial animation is complete
    if (!hasAnimated) return;
    
    // Update slider position immediately on click
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const clampedPercentage = Math.max(0, Math.min(100, percentage));
      setSliderPosition(clampedPercentage);
      setGlyphFromPercent(clampedPercentage);
    }
    
    isDraggingRef.current = true;
    setIsDragging(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
  }, [handleMouseMove, handleMouseUp, setGlyphFromPercent, hasAnimated]);

  const handleTouchStart = useCallback((e) => {
    // Only allow touch interaction after the initial animation is complete
    if (!hasAnimated) return;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      timestamp: Date.now()
    };
    gestureDirectionRef.current = null; // Reset gesture direction
    
    // Don't prevent default here - let the browser handle it initially
  }, [hasAnimated]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || !containerRef.current || !hasAnimated) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // Determine gesture direction if not already determined
    if (!gestureDirectionRef.current) {
      const threshold = 10; // Minimum movement to determine direction
      
      if (deltaX > threshold || deltaY > threshold) {
        if (deltaX > deltaY) {
          gestureDirectionRef.current = 'horizontal';
        } else {
          gestureDirectionRef.current = 'vertical';
        }
      }
    }
    
    // Only handle horizontal gestures for the slider
    if (gestureDirectionRef.current === 'horizontal') {
      e.preventDefault(); // Prevent scrolling only for horizontal gestures
      
      // Update slider position immediately on first horizontal touch
      if (!isDraggingRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        const clampedPercentage = Math.max(0, Math.min(100, percentage));
        setSliderPosition(clampedPercentage);
        setGlyphFromPercent(clampedPercentage);
        
        isDraggingRef.current = true;
        setIsDragging(true);
      }
      
      // Continue updating slider position
      const rect = containerRef.current.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const clampedPercentage = Math.max(0, Math.min(100, percentage));
      
      setSliderPosition(clampedPercentage);
      setGlyphFromPercent(clampedPercentage);
    }
    // For vertical gestures, do nothing - let the browser handle scrolling
  }, [setGlyphFromPercent, hasAnimated]);

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    touchStartRef.current = null;
    gestureDirectionRef.current = null;
  }, []);

  // Cleanup event listeners and restore user select
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [handleMouseMove, handleMouseUp]);

  // Restore user select when dragging stops
  useEffect(() => {
    if (!isDragging) {
      document.body.style.userSelect = '';
    }
  }, [isDragging]);

  // Set initial glyph
  useEffect(() => {
    setGlyphFromPercent(sliderPosition);
  }, [setGlyphFromPercent, sliderPosition]);

  // Animation effect when component becomes visible in viewport
  useEffect(() => {
    if (!containerRef.current) return;

    console.log(`Setting up Intersection Observer for slider: ${sliderKey}`);

    // Create Intersection Observer to detect when slider becomes visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated) {
          console.log(`Slider ${sliderKey} became visible, starting animation`);
          setHasAnimated(true);
          
          // Animate slider from 0% to 50% over 1 second
          gsap.to(animationRef.current, {
            duration: 1.5,
            value: 50,
            ease: "circ.out",
            onUpdate: function() {
              setSliderPosition(animationRef.current.value);
              setGlyphFromPercent(animationRef.current.value);
            }
          });
        }
      });
    }, {
      threshold: 0.3, // Trigger when 30% of the slider is visible
      rootMargin: "0px 0px -20% 0px" // Trigger when slider is 20% from bottom of viewport
    });

    // Start observing the slider
    observer.observe(containerRef.current);

    return () => {
      console.log(`Cleaning up Intersection Observer for slider: ${sliderKey}`);
      observer.disconnect();
    };
  }, [sliderKey, hasAnimated, setGlyphFromPercent]);

  // Reset animation state when component mounts or when brand changes
  useEffect(() => {
    setHasAnimated(false);
    setSliderPosition(0);
    setGlyphFromPercent(0);
    animationRef.current.value = 0;
  }, [sliderKey]); // Reset when sliderKey changes (new brand/slider instance)

  const handleFullscreenToggle = useCallback(() => {
    const el = containerRef.current;
    if (!isFullscreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [isFullscreen]);

  useEffect(() => {
    function fullscreenChangeHandler() {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    }
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);
    };
  }, []);

  // Calculate aspect ratio for container height - responsive
  const getAspectRatio = () => {
    if (aspectRatio && aspectRatio !== "3:4") {
      const [width, height] = aspectRatio.split(':').map(Number);
      return width / height;
    }
    // Default: 4:3 on desktop, 3:4 on mobile
    return window.innerWidth >= 992 ? 4/3 : 3/4;
  };

  const [calculatedAspectRatio, setCalculatedAspectRatio] = useState(getAspectRatio());

  // Update aspect ratio on window resize
  useEffect(() => {
    const handleResize = () => {
      if (!aspectRatio || aspectRatio === "3:4") {
        setCalculatedAspectRatio(window.innerWidth >= 992 ? 4/3 : 3/4);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [aspectRatio]);

  const containerStyle = { 
    width: '100%',
    height: customHeight || (aspectRatio === "auto" ? '100%' : 'auto'),
    minHeight: minHeight || undefined,
    maxHeight: customMaxHeight || undefined,
    aspectRatio: (aspectRatio === "auto" || minHeight) ? undefined : calculatedAspectRatio,
    borderRadius: borderRadius,
    overflow: 'hidden',
    position: 'relative',
    cursor: hasAnimated ? 'col-resize' : 'default'
  };

  // Debug log to see the computed styles
  console.log(`ABComparisonSlider ${id} computed styles:`, containerStyle);

  return (
    <div className={`ab-comparison-slider${isFullscreen ? ' fullscreen' : ''}`}>
      <div 
        ref={containerRef}
        className={`comparison-container ${isDragging ? 'dragging' : ''}`}
        style={containerStyle}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Before Image (Full width) */}
        <div className="image-container before-image">
          <img
            ref={beforeImageRef}
            src={beforeImage}
            alt={beforeLabel}
            className="comparison-image"
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top'
            }}
          />
        </div>

        {/* After Image (Clipped by slider) */}
        <div 
          className="image-container after-image"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            ref={afterImageRef}
            src={afterImage}
            alt={afterLabel}
            className="comparison-image"
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top'
            }}
          />
        </div>

        {/* Slider Handle */}
        <div 
          className="slider-handle"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="slider-line"></div>
          {/* Ghost glyph on the left */}
          <svg
            className="slider-icon ghost-left"
            width="6"
            height="6"
            fill="var(--c4)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[getPreviousGlyph(currentGlyph)] }}
          />
          {/* Current glyph */}
          <svg
            className="slider-icon"
            width="6"
            height="6"
            fill="var(--c5)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[currentGlyph] }}
          />
          {/* Ghost glyph on the right */}
          <svg
            className="slider-icon ghost-right"
            width="6"
            height="6"
            fill="var(--c4)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[getNextGlyph(currentGlyph)] }}
          />
          {/* Bottom glyphs - mirrored */}
          {/* Ghost glyph on the left (bottom) */}
          <svg
            className="slider-icon ghost-left bottom"
            width="6"
            height="6"
            fill="var(--c4)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[getPreviousGlyph(currentGlyph)] }}
          />
          {/* Current glyph (bottom) */}
          <svg
            className="slider-icon bottom"
            width="6"
            height="6"
            fill="var(--c5)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[currentGlyph] }}
          />
          {/* Ghost glyph on the right (bottom) */}
          <svg
            className="slider-icon ghost-right bottom"
            width="6"
            height="6"
            fill="var(--c4)"
            viewBox="0 0 24 24"
            dangerouslySetInnerHTML={{ __html: glyphPaths[getNextGlyph(currentGlyph)] }}
          />
        </div>

        {/* Slider Track */}
        <div className="slider-track"></div>
        {/* Fullscreen Expander Button */}
        <button
          className={`slider-fullscreen-btn${isFullscreen ? ' active' : ''}`}
          type="button"
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          onClick={handleFullscreenToggle}
          tabIndex={0}
          style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, padding: '0.35rem' }}
        >
          <GlyphSvg color={"var(--c0)"} glyph={'g06'} size={"20"} />
        </button>
      </div>
      
      {/* Caption */}
      {caption && (
        <p className="fs-8 text-muted mt-2 text-center">{caption}</p>
      )}
    </div>
  );
};

export default ABComparisonSlider; 